/** Static config for the 7 crew dashboard archetypes. Each archetype
 *  declares its title strip, accent colour, workflow widget list and
 *  quick actions. The shared <CrewShell/> renders these into a
 *  consistent visual grid so all seven feel like one product family
 *  while surfacing role-appropriate work.
 *
 *  Widget shape:
 *    { id, label, icon, hint, href, ping? } — `ping` is a small badge
 *    string used for counts (e.g. "3 pending"). Runtime counts are
 *    injected by the shell after live API calls.
 *
 *  Quick-action shape:
 *    { id, label, href, primary? } — `primary` renders as filled CTA.
 */
import {
  Film, ClipboardList, Users, Coins, Radar, Sparkles,
  Camera, Calendar, Inbox, Star, Clapperboard,
  Mic, Bookmark, TrendingUp,
  ScrollText, QrCode, Wallet, ShieldCheck,
  Megaphone, Award, PieChart, Briefcase,
  Building2, FileText, UserCog, HandCoins,
  Rocket, GraduationCap, Bell, HeartHandshake,
} from "lucide-react";

export const ARCHETYPES = {
  /* ─── 1. FILMMAKER ─────────────────────────────────────── */
  filmmaker: {
    key:    "filmmaker",
    title:  "Filmmaker · Console",
    accent: "#F5B82E",
    tagline: "Green-light. Assemble. Ship.",
    workflow: [
      { id: "projects",   label: "Active Projects",     icon: Film,          href: "/projects" },
      { id: "callsheets", label: "Call Sheets Pending", icon: ClipboardList, href: "/projects?filter=callsheet_pending" },
      { id: "crew",       label: "Crew Invites",        icon: Users,         href: "/dashboard/crew-invites" },
      { id: "budget",     label: "Budget Watchlist",    icon: Coins,         href: "/projects?view=finance" },
      { id: "discovery",  label: "Gatekeeper Hits",     icon: Radar,         href: "/gatekeeper" },
    ],
    quickActions: [
      { id: "new-project", label: "Start a Project",     href: "/onboard-project", primary: true },
      { id: "invite-crew", label: "Invite Crew",         href: "/crew" },
      { id: "browse-unions", label: "Browse Unions",     href: "/associations" },
    ],
  },

  /* ─── 2. CRAFT ─────────────────────────────────────────── */
  craft: {
    key:    "craft",
    title:  "Craft · Console",
    accent: "#5EEAD4",
    tagline: "Bookings. Kit. Reels.",
    workflow: [
      { id: "assignments", label: "Upcoming Assignments", icon: Calendar, href: "/schedule" },
      { id: "inbox",       label: "Invitation Inbox",     icon: Inbox,    href: "/dashboard/invites" },
      { id: "availability",label: "Availability",         icon: Clapperboard, href: "/profile/availability" },
      { id: "portfolio",   label: "Portfolio Views",      icon: Star,     href: "/profile" },
      { id: "union",       label: "Union Card",           icon: ShieldCheck, href: "/icard" },
    ],
    quickActions: [
      { id: "reel",  label: "Update Reel",   href: "/profile#reel",       primary: true },
      { id: "avail", label: "Set Availability", href: "/profile#availability" },
      { id: "rate",  label: "Set Rate Card", href: "/profile#rate" },
    ],
  },

  /* ─── 3. TALENT ────────────────────────────────────────── */
  talent: {
    key:    "talent",
    title:  "Talent · Console",
    accent: "#FB7185",
    tagline: "Audition. Callback. Camera.",
    workflow: [
      { id: "shoots",     label: "Upcoming Shoots",   icon: Camera,     href: "/schedule" },
      { id: "auditions",  label: "Audition Invites",  icon: Inbox,      href: "/dashboard/auditions" },
      { id: "agent",      label: "Agent Inbox",       icon: HeartHandshake, href: "/dashboard/agent" },
      { id: "impressions",label: "Casting Views",     icon: TrendingUp, href: "/profile" },
      { id: "bookmarks",  label: "CD Bookmarks",      icon: Bookmark,   href: "/profile/bookmarks" },
    ],
    quickActions: [
      { id: "headshots",   label: "Update Headshots", href: "/profile#headshots", primary: true },
      { id: "showreel",    label: "Update Showreel",  href: "/profile#reel" },
      { id: "agent",       label: "Attach Agent",     href: "/profile#agent" },
    ],
  },

  /* ─── 4. LABOR / DAILY-WAGE ────────────────────────────── */
  labor: {
    key:    "labor",
    title:  "Daily Wage · Console",
    accent: "#A78BFA",
    tagline: "Muster. Wages. Health.",
    workflow: [
      { id: "attendance", label: "Attendance",     icon: QrCode,      href: "/attendance-heatmap" },
      { id: "wages",      label: "Wage Ledger",    icon: Wallet,      href: "/dashboard/wages" },
      { id: "gigs",       label: "Active Gigs",    icon: ClipboardList, href: "/dashboard/gigs" },
      { id: "welfare",    label: "PF / ESI",       icon: ShieldCheck, href: "/dashboard/welfare" },
      { id: "safety",     label: "Set Safety",     icon: Bell,        href: "/dashboard/safety" },
    ],
    quickActions: [
      { id: "muster", label: "Mark Muster",   href: "/attendance", primary: true },
      { id: "wages",  label: "View Wages",    href: "/dashboard/wages" },
      { id: "card",   label: "Show Union Card", href: "/icard" },
    ],
  },

  /* ─── 5. AGENCY-STAFF ──────────────────────────────────── */
  agency_staff: {
    key:    "agency_staff",
    title:  "Agency · Console",
    accent: "#38BDF8",
    tagline: "Brief. Concept. Campaign.",
    cobrandNote: "Co-branded with your agency",
    workflow: [
      { id: "brands",   label: "Assigned Brands",    icon: Briefcase,  href: "/dashboard/brands" },
      { id: "pipeline", label: "Campaign Pipeline",  icon: Megaphone,  href: "/dashboard/campaigns" },
      { id: "awards",   label: "Awards Feed",        icon: Award,      href: "/dashboard/awards" },
      { id: "team",     label: "Team Huddle",        icon: Users,      href: "/dashboard/team" },
      { id: "insights", label: "Media / Insights",   icon: PieChart,   href: "/dashboard/insights" },
    ],
    quickActions: [
      { id: "case-study", label: "Add Case Study",  href: "/profile#portfolio", primary: true },
      { id: "awards",     label: "Log Award",       href: "/profile#awards" },
      { id: "team",       label: "See Team",        href: "/dashboard/team" },
    ],
  },

  /* ─── 6. OFFICE-STAFF (Managerial · Non-Agency) ────────── */
  office_staff: {
    key:    "office_staff",
    title:  "Office · Console",
    accent: "#E4B4FF",
    tagline: "Queues. Ledgers. Contracts.",
    cobrandNote: "Co-branded with your company",
    // Widgets adapt by department_key at runtime — the shell reads
    // this table and picks the matching row based on user.department_key.
    workflowByDept: {
      accounts_finance: [
        { id: "payments",  label: "Payments Queue",  icon: HandCoins,   href: "/dashboard/payments" },
        { id: "invoices",  label: "Invoices",        icon: FileText,    href: "/dashboard/invoices" },
        { id: "reimburse", label: "Reimbursements",  icon: Wallet,      href: "/dashboard/reimbursements" },
        { id: "tds",       label: "TDS / GST",       icon: ShieldCheck, href: "/dashboard/tax" },
        { id: "payroll",   label: "Payroll",         icon: Coins,       href: "/dashboard/payroll" },
      ],
      hr_admin: [
        { id: "joiners",   label: "New Joiners",     icon: UserCog,     href: "/dashboard/joiners" },
        { id: "contracts", label: "Contract Library",icon: FileText,    href: "/dashboard/contracts" },
        { id: "leave",     label: "Leave Tracker",   icon: Calendar,    href: "/dashboard/leave" },
        { id: "muster",    label: "Attendance",      icon: QrCode,      href: "/attendance-heatmap" },
        { id: "policies",  label: "HR Policies",     icon: ScrollText,  href: "/dashboard/policies" },
      ],
      legal_compliance: [
        { id: "vault",     label: "Contract Vault",  icon: FileText,    href: "/dashboard/contracts" },
        { id: "licenses",  label: "Licence Status",  icon: ShieldCheck, href: "/dashboard/licenses" },
        { id: "disputes",  label: "Dispute Tracker", icon: Bell,        href: "/dashboard/disputes" },
        { id: "policies",  label: "Compliance",      icon: ScrollText,  href: "/dashboard/compliance" },
      ],
      vendor_ops: [
        { id: "perf",      label: "Vendor Performance", icon: PieChart, href: "/dashboard/vendor-perf" },
        { id: "po",        label: "PO Tracker",       icon: FileText,   href: "/dashboard/po" },
        { id: "dispatch",  label: "Dispatch Board",   icon: ClipboardList, href: "/dashboard/dispatch" },
        { id: "audits",    label: "Site Audits",      icon: ShieldCheck, href: "/dashboard/audits" },
      ],
    },
    quickActions: [
      { id: "invite-teammate", label: "Invite Teammate", href: "/dashboard/team", primary: true },
      { id: "profile",         label: "Edit Profile",    href: "/profile" },
    ],
  },

  /* ─── 7. CREATOR & SUPPORT ─────────────────────────────── */
  creator_support: {
    key:    "creator_support",
    title:  "Creator & Support · Console",
    accent: "#FBBF24",
    tagline: "Content. Deliverables. Learning.",
    // Same shape as office_staff — one table adapts to department.
    workflowByDept: {
      content_creator: [
        { id: "deals",      label: "Brand Deals Inbox", icon: HandCoins,  href: "/dashboard/deals" },
        { id: "calendar",   label: "Content Calendar",  icon: Calendar,   href: "/dashboard/calendar" },
        { id: "payouts",    label: "Sponsor Payouts",   icon: Wallet,     href: "/dashboard/payouts" },
        { id: "stats",      label: "Social Stats",      icon: TrendingUp, href: "/profile#social" },
        { id: "ratecard",   label: "Rate Card",         icon: Star,       href: "/profile#rate" },
      ],
      support_staff: [
        { id: "shift",      label: "Shift Schedule",   icon: Calendar,    href: "/dashboard/shifts" },
        { id: "muster",     label: "Daily Muster",     icon: QrCode,      href: "/attendance" },
        { id: "salary",     label: "Salary Slip",      icon: FileText,    href: "/dashboard/salary" },
        { id: "announce",   label: "Announcements",    icon: Bell,        href: "/dashboard/announcements" },
      ],
      trainee: [
        { id: "onboard",    label: "Onboarding Checklist", icon: ClipboardList, href: "/dashboard/onboarding" },
        { id: "mentor",     label: "Mentor",              icon: HeartHandshake, href: "/dashboard/mentor" },
        { id: "learn",      label: "Learning Resources",  icon: GraduationCap,  href: "/dashboard/learn" },
        { id: "milestones", label: "Milestones",          icon: Rocket,         href: "/dashboard/milestones" },
      ],
    },
    quickActions: [
      { id: "profile", label: "Complete Profile", href: "/profile", primary: true },
    ],
  },
};

export const DEFAULT_ARCHETYPE = "craft"; // safe fallback
